<template>
  <page-layout
    :wrap-styles="{
      padding: '24px',
    }"
    :styles="{
      padding: '0px',
    }"
  >
    <a-alert
      v-if="currentError"
      class="workflow-error"
      type="error"
      show-icon
    >
      <span slot="message">
        <span class="workflow-error__label">{{ $t('k_ad17f965', { arg_0: $t('k_be2f741e') }) }}</span>
        {{ currentError }}
      </span>
    </a-alert>
    <a-card
      v-if="isUnknownFlowStatus"
      :title="$t('k_481bdccc')"
    >
      <a-descriptions :column="1">
        <a-descriptions-item :label="$t('k_686301fd')">
          {{ flowName }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('k_9c96e49f')">
          {{ entity.creator }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('k_f69403f5')">
          {{ applicationTime }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('k_cf8cccf9')">
          {{ entity.statusName }}({{ entity.status }})
        </a-descriptions-item>
        <a-descriptions-item :label="$t('k_655aac6a')">
          {{ $t('k_64f650a2') }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('k_5cadee77')">
          {{ entity.handlers }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('k_79f63bf')">
          <a-popover
            trigger="click"
            placement="bottom"
            overlay-class-name="workflow-payload-popover"
          >
            <pre
              slot="content"
              class="workflow-payload"
            >{{ payloadText }}</pre>
            <a href="javascript:;">{{ $t('k_b29e29fa') }}</a>
          </a-popover>
        </a-descriptions-item>
      </a-descriptions>
    </a-card>
    <div v-else-if="isPermissionApply && (isMobileScreen ? entity.flowStatus === 'processing' : true)">
      <div
        v-if="!isMobileScreen"
        style="display:grid;grid-template-columns:1fr 1fr;gap:0 12px"
      >
        <apply-approval
          :project-id="projectId"
          :env="env"
          :entity="entity"
        />
        <w-sys-page
          :config="getPageConfig()"
          :project-config="projectConfig"
          style="background-color:transparent"
        />
      </div>
      <div v-else>
        <apply-approval
          :project-id="projectId"
          :env="env"
          :entity="entity"
        />
      </div>
    </div>
    <w-sys-page
      v-else
      :config="getPageConfig()"
      :project-config="projectConfig"
      style="background-color:transparent"
    />
  </page-layout>
</template>

<script>
import dayjs from 'dayjs';
import { $t } from '@/i18n';
import { isMobileOrNarrowScreen } from '@utils/screen';
import { APPLY_WORKFLOW_ID } from '@pages/modules/permission/constants';
import ApplyApproval from '../../permission/ApplyApproval.vue';
import { BUILTIN_PAGE_ID } from '@config/constant';
import { getWorkflowNotificationPluginList } from '@pages/modules/workflow/config/utils';
import './payload-popover.scss';

export default {
  components: {
    ApplyApproval,
  },
  props: {
    projectId: {
      type: String,
      default: '',
    },
    env: {
      type: String,
      default: '',
    },
    entity: {
      type: Object,
      default: null,
    },
    records: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      projectConfig: window.xy_runtime_project,
    };
  },
  computed: {
    isPermissionApply() {
      return this.checkIsPermissionApply();
    },
    payload() {
      if (!this.entity) return null;
      const { payload } = this.entity;
      return JSON.parse(payload);
    },
    isMobileScreen() {
      return isMobileOrNarrowScreen();
    },
    withDefaultAction() {
      if (!this.entity) return false;
      const flowConf = JSON.parse(this.entity.flowConf);
      const pluginId = flowConf.notifyPluginId;
      if (!pluginId) return true;
      const plugin = getWorkflowNotificationPluginList().find(x => x.id === pluginId);
      return !plugin?.disableDefaultAction;
    },
    isUnknownFlowStatus() {
      return this.entity && !['processing', 'success', 'fail', 'abort'].includes(this.entity.flowStatus);
    },
    flowName() {
      return this.entity ? JSON.parse(this.entity.flowConf).name : '';
    },
    applicationTime() {
      return this.entity ? dayjs(this.entity.ctime).format('YYYY-MM-DD HH:mm:ss') : '';
    },
    payloadText() {
      return this.payload ? JSON.stringify(this.payload, null, 2) : '';
    },
    currentError() {
      if (!this.entity || ['success', 'abort'].includes(this.entity.flowStatus)) return '';

      const currentRecords = this.records
        .filter(({ _ctime: ctime }) => (
          !this.entity.startTime
          || dayjs(ctime).format('YYYYMMDDHHmmss') >= this.entity.startTime.slice(0, 14)
        ))
        .sort(({ _ctime: leftTime }, { _ctime: rightTime }) => (
          dayjs(leftTime).valueOf() - dayjs(rightTime).valueOf()
        ));
      let errorIndex = -1;
      for (let index = currentRecords.length - 1; index >= 0; index -= 1) {
        if (currentRecords[index].error) {
          errorIndex = index;
          break;
        }
      }
      if (errorIndex < 0) return '';

      const errorRecord = currentRecords[errorIndex];
      const flow = JSON.parse(this.entity.flowConf);
      const abnormalNodeIds = flow.nodes.filter(node => node.abnormal).map(node => node.id);
      const hasSucceededLater = currentRecords.slice(errorIndex + 1).some((record) => {
        if (record.error || record.from !== errorRecord.from) return false;
        const isFailClosedCompanion = abnormalNodeIds.includes(record.to)
          && record.operation === errorRecord.operation
          && record.creator === errorRecord.creator
          && !record.payload;
        return !isFailClosedCompanion;
      });
      return hasSucceededLater ? '' : errorRecord.error;
    },
  },
  methods: {
    checkIsPermissionApply() {
      if (!this.entity) return false;
      const { flowId } = this.entity;
      return flowId.endsWith(APPLY_WORKFLOW_ID);
    },
    getPageConfig() {
      return {
        projectId: this.projectId,
        pageId: BUILTIN_PAGE_ID,
        title: $t('k_481bdccc'),
        pageData: {},
        pageConfig: [
          {
            id: 'default',
            name: 'mainPage',
            ucVersion: 1,
            xyType: 'pagelet',
            lessCode: 'export default {\n  data() {\n    return {\n    };\n  },\n  mounted(){\n  },\n  methods: {\n  },\n};\n',
            layout: {
              type: 'w-container',
              props: {},
              children: [
                {
                  type: 'w-row',
                  children: [
                    {
                      type: 'w-col',
                      children: [
                        {
                          type: 'w-card',
                          props: {
                            title: $t('k_481bdccc'),
                          },
                          children: [
                            {
                              type: 'public-w-workflow-detail:antd',
                              props: {
                                hideTitle: true,
                                isNarrowScreen: isMobileOrNarrowScreen(),
                                defaultEntity: this.entity,
                              },
                            },
                          ],
                        },
                        ...(!this.isPermissionApply && this.withDefaultAction && this.entity?.flowStatus === 'processing' ? [{
                          type: 'w-card',
                          children: [
                            {
                              type: 'public-w-workflow-approve',
                            },
                          ],
                          props: {
                            css: `width: ${isMobileOrNarrowScreen() ? '100%' : '50%'}`,
                          },
                        }] : []),
                      ],
                      props: {},
                    },
                  ],
                },
              ],
            },
          },
        ],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.workflow-error {
  margin-bottom: 16px;
  white-space: pre-wrap;
  word-break: break-word;
}

.workflow-error__label {
  font-weight: 500;
}

.workflow-payload {
  box-sizing: border-box;
  max-width: min(776px, calc(100vw - 48px));
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-wrap: anywhere;
}
</style>
